import { FieldNames, FormType } from "@/enums";
import { ISelectOption } from "@/interfaces";

import { UserOptionLabel, ViperSelect } from "@/components";
import { IIntakeApprover } from "@/interfaces/Project/Roles/IIntakeApprover";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import { useFormPropertyValueLookupProvider } from "@/providers";
import {
  useGetFormLookupsQuery,
  useGetUserQuery,
  useSuggestUsersQuery,
} from "@/services";
import { Button, Form, Modal, Select, Spin } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";

const { Option } = Select;

interface IProps {
  regionRole: IRegionalProjectRole;
  open: boolean;
  onClose(): void;
  onAddAnother(data: IIntakeApprover): void;
  onOk(data: IIntakeApprover): void;
  intakeApproverId?: string;
}

export const AddEditIntakeApproverModal = ({
  regionRole,
  open,
  onClose,
  onAddAnother,
  onOk,
  intakeApproverId,
}: IProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [userQuery, setUserQuery] = useState<string>("");
  const [productCategories, setProductCategories] = useState<ISelectOption[]>(
    []
  );

  const { data: userSuggestions, isFetching: fetchingSuggestions } =
    useSuggestUsersQuery(
      { query: userQuery },
      {
        skip: !userQuery,
      }
    );

  const { data: forms } = useGetFormLookupsQuery();

  const [workingIntakeApprover, setWorkingIntakeApprover] =
    useState<IIntakeApprover>();

  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  useEffect(() => {
    setWorkingIntakeApprover(
      regionRole.intakeApprovers.find((x) => x.id === intakeApproverId)
    );
  }, [intakeApproverId, regionRole.intakeApprovers]);

  const { data: user, isFetching } = useGetUserQuery(
    workingIntakeApprover?.userId,
    {
      skip:
        !workingIntakeApprover?.userId || workingIntakeApprover?.userId === "",
    }
  );
  useEffect(() => {
    const intake = forms?.intake;
    if (!intake) return;

    setProductCategories(
      Object.keys(intake?.productCategory).map((key) => ({
        value: key,
        label: getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProductCategory,
          key
        ),
      }))
    );
  }, [forms?.intake, getPropertyValueDisplay]);
  const handleAdd = (addAnother: boolean): void => {
    form.validateFields().then((data: { userId: string; publicProductCategoryIds: string[] }) => {
      const newId = v4();
        
      if (addAnother) {
       onAddAnother({ id: newId, publicId: newId, ...data });
      } else {
        onOk({
         id: workingIntakeApprover?.id ?? newId,
         publicId: workingIntakeApprover?.id ?? newId,
          ...data,
         userId:
            typeof form.getFieldValue("userId") === "string"
              ? form.getFieldValue("userId")
              : workingIntakeApprover?.userId ?? data.userId,
        });
      }

      form.resetFields();
    });
  };

  const isDuplicateUser = (value: string): boolean =>
    regionRole.intakeApprovers.some(
      (approver) =>
        approver.userId === value &&
        approver.userId !== workingIntakeApprover?.userId
    );

  return (
    <Modal
      centered
      destroyOnHidden={true}
      closable={false}
      title={intl.formatMessage({
        id: workingIntakeApprover?.id
          ? "projectRoles.editIntakeApprover"
          : "projectRoles.addIntakeApprover",
      })}
      open={open}
      footer={
        workingIntakeApprover?.id
          ? [
              <Button
                key="cancel"
                data-test-id="intake-approver-modal-cancel"
                onClick={onClose}
              >
                {intl.formatMessage({ id: "general.cancel" })}
              </Button>,
              <Button
                key="edit"
                type="primary"
                data-test-id="intake-approver-modal-edit"
                onClick={(): void => handleAdd(false)}
              >
                {intl.formatMessage({ id: "general.edit" })}
              </Button>,
            ]
          : [
              <Button
                key="cancel"
                data-test-id="intake-approver-modal-cancel"
                onClick={onClose}
              >
                {intl.formatMessage({ id: "general.cancel" })}
              </Button>,
              <Button
                key="addAnother"
                data-test-id="intake-approver-modal-add-another"
                onClick={(): void => handleAdd(true)}
              >
                {intl.formatMessage({ id: "projectRoles.addAnotherUser" })}
              </Button>,
              <Button
                key="add"
                type="primary"
                data-test-id="intake-approver-modal-add"
                onClick={(): void => handleAdd(false)}
              >
                {intl.formatMessage({ id: "general.add" })}
              </Button>,
            ]
      }
    >
      {!isFetching ? (
        <Form form={form} layout="vertical" preserve={false}>
          {workingIntakeApprover?.id ? (
            <Form.Item
              name="userId"
              label={intl.formatMessage({ id: "projectRoles.userName" })}
              initialValue={{ label: user?.displayName, value: user?.userId }}
              rules={[
                { required: true, message: "" },
                {
                  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                  validator(_, value) {
                    if (value && isDuplicateUser(value)) {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: "projectRoles.duplicateUserError",
                          })
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <ViperSelect
                optionLabelProp="label"
                customOptions={userSuggestions?.map(
                  ({
                    document: { id, displayName, department, email, jobTitle },
                  }) => (
                    <Option key={id} label={displayName}>
                      <UserOptionLabel
                        displayName={displayName}
                        department={department}
                        email={email}
                        jobTitle={jobTitle}
                      />
                    </Option>
                  )
                )}
                loading={fetchingSuggestions}
                value={workingIntakeApprover?.userId}
                onFetchSuggestions={(searchTerm): void => {
                  setUserQuery(searchTerm);
                }}
                data-test-id="add-users-dialog-users-add"
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="userId"
              label={intl.formatMessage({ id: "projectRoles.userName" })}
              rules={[
                { required: true, message: "" },
                {
                  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                  validator(_, value) {
                    if (value && isDuplicateUser(value)) {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: "projectRoles.duplicateUserError",
                          })
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <ViperSelect
                optionLabelProp="label"
                customOptions={userSuggestions?.map(
                  ({
                    document: { id, displayName, department, email, jobTitle },
                  }) => (
                    <Option key={id} label={displayName}>
                      <UserOptionLabel
                        displayName={displayName}
                        department={department}
                        email={email}
                        jobTitle={jobTitle}
                      />
                    </Option>
                  )
                )}
                loading={fetchingSuggestions}
                value={workingIntakeApprover?.userId}
                onFetchSuggestions={(searchTerm): void => {
                  setUserQuery(searchTerm);
                }}
                data-test-id="add-users-dialog-users-add"
              />
            </Form.Item>
          )}

          <Form.Item
            name="publicProductCategoryIds"
            label={intl.formatMessage({ id: "projectRoles.productCategory" })}
            rules={[{ required: true, message: "" }]}
            initialValue = { workingIntakeApprover?.publicProductCategoryIds }
          >
            <Select allowClear mode="multiple" options={productCategories} />
          </Form.Item>
        </Form>
      ) : (
        <Spin />
      )}
    </Modal>
  );
};
